import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import type { ComponentProps } from "react"

import { Separator } from "~/components/reusable/Separator"
import { cn } from "~/utils/helpers"

const buttonGroupVariants = cva(
  cn(
    "flex w-fit items-stretch shadow-button-neutral",
    "[&>[data-slot]:not(:focus-visible)]:shadow-none!",
    "*:data-slot:rounded-none! *:focus-visible:relative *:focus-visible:z-10",
    "[&>[data-slot]:not([data-slot=button-group-separator])+[data-slot]:not([data-slot=button-group-separator])]:relative [&>[data-slot]:not([data-slot=button-group-separator])+[data-slot]:not([data-slot=button-group-separator])]:after:pointer-events-none [&>[data-slot]:not([data-slot=button-group-separator])+[data-slot]:not([data-slot=button-group-separator])]:after:absolute [&>[data-slot]:not([data-slot=button-group-separator])+[data-slot]:not([data-slot=button-group-separator])]:after:bg-border-base",
  ),
  {
    variants: {
      orientation: {
        horizontal: cn(
          "rounded-full",
          "[&>[data-slot]:first-child]:rounded-l-full! [&>[data-slot]:last-child]:rounded-r-full!",
          "[&>[data-slot]:not([data-slot=button-group-separator])+[data-slot]:not([data-slot=button-group-separator])]:after:inset-y-0 [&>[data-slot]:not([data-slot=button-group-separator])+[data-slot]:not([data-slot=button-group-separator])]:after:left-0 [&>[data-slot]:not([data-slot=button-group-separator])+[data-slot]:not([data-slot=button-group-separator])]:after:w-px",
        ),
        vertical: cn(
          "flex-col rounded-lg *:data-slot:w-full",
          "[&>[data-slot]:first-child]:rounded-t-lg! [&>[data-slot]:last-child]:rounded-b-lg!",
          "[&>[data-slot]:not([data-slot=button-group-separator])+[data-slot]:not([data-slot=button-group-separator])]:after:inset-x-0 [&>[data-slot]:not([data-slot=button-group-separator])+[data-slot]:not([data-slot=button-group-separator])]:after:top-0 [&>[data-slot]:not([data-slot=button-group-separator])+[data-slot]:not([data-slot=button-group-separator])]:after:h-px",
        ),
      },
    },
    defaultVariants: {
      orientation: "horizontal",
    },
  },
)

type ButtonGroupProps = ComponentProps<"div"> & VariantProps<typeof buttonGroupVariants>

/**
 * Joins `Button` / `IconButton` children into one control: the group carries the outline
 * (`shadow-button-neutral`), the children lose theirs and their inner corners, and a 1px
 * `border-base` divider separates neighbours. Children keep their own variant, size, `href`
 * and handlers. Canonical intent: {@link ../design-system/intents.ts BUTTON_GROUP_INTENT}.
 * Approved surface: design-system/APPROVED.md.
 */
function ButtonGroup({ className, orientation, ...props }: ButtonGroupProps) {
  return (
    <div
      role="group"
      data-slot="button-group"
      data-orientation={orientation ?? "horizontal"}
      className={cn(buttonGroupVariants({ orientation }), className)}
      {...props}
    />
  )
}

/** Non-interactive label segment inside a `ButtonGroup`. `asChild` renders your element. */
function ButtonGroupText({
  className,
  asChild = false,
  ...props
}: ComponentProps<"div"> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : "div"
  return (
    <Comp
      data-slot="button-group-text"
      className={cn(
        "flex items-center gap-1.5 bg-component px-2 label-base text-fg-subtle",
        "[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:fill-fg-subtle",
        className,
      )}
      {...props}
    />
  )
}

/** Explicit divider between segments, for groups whose neighbours shouldn't touch. */
function ButtonGroupSeparator({
  className,
  orientation = "vertical",
  ...props
}: ComponentProps<typeof Separator>) {
  return (
    <Separator
      data-slot="button-group-separator"
      orientation={orientation}
      className={cn("self-stretch", orientation === "vertical" ? "h-auto" : "w-auto", className)}
      {...props}
    />
  )
}

export { ButtonGroup, ButtonGroupSeparator, ButtonGroupText, buttonGroupVariants }
export type { ButtonGroupProps }
